(ns machtfit.dashboard-widgets
  (:require ["react-quill" :as ReactQuill]
            [ajax.core :refer [GET]]
            [clojure.string :as str]
            [machtfit.core :as machtfit]
            [machtfit.datetime :as datetime]
            [machtfit.dropdown :refer [single-dropdown]]
            [machtfit.human :as human]
            [machtfit.product-form.validation :as validation]
            [machtfit.translation :refer [t]]
            [reagent.core :as r]))

(defonce register-custom-video-blot
  (let [Quill (.-Quill ReactQuill)
        Video (.import Quill "formats/video")
        create* (.-create Video)]
    (set! (.-create Video)
          (fn [value]
            (let [node (.call create* Video value)]
              (.setAttribute node "referrerpolicy" "strict-origin-when-cross-origin")
              node)))
    true))

(defn- clean-positive-number
  ([positive-number default]
   (let [n (js/parseInt positive-number)]
     (if (js/isNaN n) default n)))
  ([positive-number]
   (clean-positive-number positive-number 0)))

(def type-positive-number {:validator validation/positive-number
                           :cleaner clean-positive-number})

(def type-positive-number-or-nil {:validator validation/positive-number
                                  :cleaner #(clean-positive-number % nil)})

(defn- clean-percent-number [percent-number]
  (let [n (js/parseInt percent-number)]
    (cond
      (js/isNaN n) 0
      (< n 0) 0
      (> n 100) 100
      :else n)))

(def type-percent-number {:validator validation/percent-number
                          :cleaner clean-percent-number})

(defn- clean-percent-decimal
  ([percent-number default]
   (let [n (js/parseFloat (validation/decimal-point percent-number))]
     (cond
       (js/isNaN n) default
       (< n 0) 0
       (> n 100) 100
       :else n)))
  ([percent-number]
   (clean-percent-decimal percent-number 0)))

(def type-percent-decimal {:validator validation/percent-decimal
                           :cleaner clean-percent-decimal})

(def type-percent-decimal-or-nil {:validator validation/percent-decimal
                                  :cleaner #(clean-percent-decimal % nil)})

(defn- clean-time [time]
  (or (some-> time
              human/human->time
              human/timeformat)
      time))

(def type-time {:validator validation/time
                :cleaner clean-time
                :placeholder {:de "Zeit"
                              :en "Time"}
                :width "3rem"
                :default ""})

(defn- clean-date [date]
  (or (some-> date
              datetime/string->date
              datetime/format-date)
      date))

(def type-date {:validator validation/date
                :cleaner clean-date
                :placeholder {:de "Datum"
                              :en "Date"}
                :width "5.5rem"
                :default ""})

(defn clean-amount [amount]
  (or (some-> amount
              human/human->amount
              human/amountformat)
      amount))

(def type-amount {:validator validation/amount
                  :cleaner clean-amount
                  :placeholder {:de "Betrag"
                                :en "Amount"}
                  :width "5.5rem"
                  :default ""})

(defn clean-text [text]
  (some-> text
          str/trim))

(defn clean-list-of-strings [text]
  (some->> text
           str/split-lines
           (remove str/blank?)
           (map str/trim)
           (str/join "\n")))

(defn label [{:keys [id value label-text label-style active?]}]
  [:label (merge
           {:for id}
           (when (or (not (empty? (str value)))
                     active?) {:class "active"})
           {:style (merge {:margin-right "10px"}
                          label-style)})
   label-text])

(defn quill [{:keys [value]}]
  (let [internal (r/atom value)
        external (r/atom value)
        on-change-timer (r/atom nil)]
    (fn [{:keys [value on-change]
          :as attrs}]
      (when-not (= value @external)
        (reset! internal value)
        (reset! external value))
      [:> ReactQuill
       (merge attrs
              {:value @internal
               :on-change #(let [new-value %]
                             (reset! internal new-value)
                                        ; fire on-change after 0.5s of inactivity
                                        ; if we fire it on every change, and type a bit faster, quill gets confused.
                                        ; to try that out just fire on-change directly here
                             (when @on-change-timer
                               (swap! on-change-timer js/clearTimeout))
                             (reset! on-change-timer
                                     (js/setTimeout (fn [] (when on-change (on-change new-value))) 500)))
               :theme "snow"
               :modules {:toolbar [[{:header [3, false]}]
                                   ["bold"]
                                   ["link"]
                                   [{:list "ordered"}, {:list "bullet"}]
                                   ["video"]
                                   ["image"]
                                   ["clean"]]
                         :clipboard {:matchVisual false}}})])))

(defn block-input [{:keys [id label-text placeholder value handler on-change on-blur block-style class input-style required? validators validate-virgin? label-style disabled input-class]
                    :or {block-style {}}}]
  (let [validators* (cons (if required? validation/required validation/optional) validators)
        virgin? (nil? value)
        validate? (or validate-virgin? (not virgin?))
        validation-error (when validate? (validation/validate value validators*))
        valid? (nil? validation-error)
        change-handler #(if on-change
                          (on-change %)
                          (when handler (handler [:change %])))
        blur-handler #(if on-blur
                        (on-blur)
                        (when handler (handler [:blur])))]
    [:div.input-field {:style block-style :class class}
     [:input {:id id
              :type "text"
              :placeholder placeholder
              :value value
              :disabled disabled
              :on-change #(change-handler (-> % .-target .-value))
              :on-blur blur-handler
              :style input-style
              :class [input-class (when validate?
                                    (case valid?
                                      true "valid"
                                      false "invalid"
                                      ""))]}]
     [label {:id id
             :value value
             :label-text label-text
             :label-style label-style
             :active? (some? placeholder)}]
     (when (and (not valid?) validation-error) [:span {:style {:color "red"}} validation-error])]))

(defn amount-input [attrs]
  [block-input (assoc attrs :validators [validation/amount])])

(defn radio-option [{:keys [name label-text value current-value on-select checked? disabled?]}]
  (let [id (str name "-" value)
        checked? (if (nil? checked?) (= current-value value) checked?)]
    ^{:key id}
    [:div
     [:input.with-gap {:id id
                       :name name
                       :type "radio"
                                        ; TODO: don't automatically ignore disabled? if checked?
                       :disabled (and (not checked?) disabled?)
                       :on-change #(on-select value)
                       :checked checked?}]
     [:label {:for id :class (when-not (and (not checked?) disabled?) "black-text")} label-text]]))

(defn radio-options [{:keys [name current-value choices on-select disabled?]}]
  [:<>
   (for [[value label-text] choices]
     ^{:key (str name "-" value)}
     [radio-option
      {:name name
       :label-text label-text
       :value value
       :current-value current-value
       :disabled? disabled?
       :on-select on-select}])])

(defn choice [{:keys [name intro choices current-choice on-select outro]}]
  [:<>
   [:h6 {:class "choice-intro" :style {:margin-right "1rem"}} intro]
   [:div
    [radio-options
     {:name name
      :current-value current-choice
      :choices choices
      :on-select on-select}]]
   (when outro [:div {:class "choice-outro" :style {:margin-left "1rem"}} outro])])

(defn choice-left-justified [{:keys [name intro choices current-choice on-select outro]}]
  [:<>
   [:div.right-align {:style {:float "left"
                              :padding-right "0.5rem"}} intro]
   [:div.valign-wrapper
    [:div.left
     [radio-options
      {:name name
       :current-value current-choice
       :choices choices
       :on-select on-select}]]
    (when outro [:div {:style {:margin-left "1rem"}} outro])]])

(defn section [{:keys [anchor title subtitle content]}]
  [:div.row (when anchor {:id (str "section-" anchor)})
   [:div.col.s2
    [:h4 title] subtitle]
   [:div.col.s10 {:style {:padding-top "1rem"}} content]])

(defn error-messages [errors]
  (if (= (count errors) 1)
    [:p {:style {:color "red"}} (first errors)]
    [:ul.errors
     (->> errors (map-indexed (fn [idx error]
                                ^{:key (str "error-" idx)}
                                [:li {:style {:list-style-type "disc" :color "red"}} error])))]))

(defn multi-select [{:keys [value choices on-save]}]
  (let [toggle-choice #(on-save (if (some #{%} value)
                                  (remove #{%} value)
                                  (conj value %)))]
    [:span
     (doall (for [[choice-value choice-label] choices]
              ^{:key choice-value}
              [:label
               [:input {:type "checkbox" :checked (some? (some #{choice-value} value)) :on-click #(toggle-choice choice-value)}]
               " " choice-label]))]))

(defn input
  "A text input field with validation and cleaning.

  value: the initial content of the input
  on-save: this function will be called with the new input after editing is finished - that is, on
    blur or <Enter>. Has to return a possibly cleaned value.
  style: map with CSS for the input tag
  placeholder: value for placeholder attribute of the input tag
  validators: sequence of validator functions. The input tag will have the class valid or invalid
    depending on whether the current input content is valid.
  default: a default value that will be given to on-save if the input content is invalid when
    editing is finished

  Cleaning means that the value can be changed by on-save, maybe to a canonical form. on-save should
  return this possibly modified value so that the input can be updated. The input is usually updated
  by itself after cleaning, since it is called again with the cleaned value. However if the new
  cleaned value is the same as the original value before this input was edited, it isn't called. To
  cover that case, on-save has to return the cleaned value so that the component can get at it."

  [{:keys [value]}]

  (let [internal-value (r/atom value)
        external-value (r/atom value)]
    (fn [{:keys [id value on-save type on-change on-focus on-blur style placeholder validators
                 validate-virgin? default required? read-only? cleaner multiline? autofocus? inline?
                 disabled? input-type class]
          :or {default nil
               read-only? false
               disabled? false
               style {}
               multiline? false}}]
      (when-not (= value @external-value)
        (reset! internal-value value)
        (reset! external-value value))
      (if read-only?
        [:span value]
        (let [{type-validator :validator
               type-cleaner :cleaner
               type-placeholder :placeholder
               type-width :width
               type-default :default} type
              placeholder* (or placeholder type-placeholder)
              validators* (or validators [type-validator])
              validators* (case required?
                            true (conj (seq validators*) validation/required)
                            false (conj (seq validators*) validation/optional)
                            validators*)
              virgin? (and (nil? @internal-value)
                           (nil? @external-value))
              validate? (or validate-virgin? (not virgin?))
              validation-error (when validate? (validation/validate @internal-value validators*))
              valid? (when validate? (nil? validation-error))
              cleaner* (or cleaner type-cleaner)
              default* (or default type-default)
              value-to-clean (if (and (= false valid?) (not (nil? default*)))
                               default*
                               @internal-value)
              clean-value (if cleaner*
                            (cleaner* value-to-clean)
                            value-to-clean)
              attrs (into {:value @internal-value
                           :on-change #(let [new-value (-> % .-target .-value)]
                                         (reset! internal-value new-value)
                                         (when on-change
                                           (on-change new-value))
                                         nil)
                           :on-blur #(do
                                       (reset! internal-value clean-value)
                                       (when on-save (on-save clean-value))
                                       (when on-blur (on-blur))
                                       nil)
                           :type input-type
                           :on-focus #(do (when on-focus
                                            (on-focus))
                                          nil)
                           :on-key-up #(do (when (and (= 13 (.-keyCode %)) (not multiline?))
                                             (-> % .-target .blur)
                                             true)
                                           nil)
                           :placeholder (if (map? placeholder*)
                                          (t placeholder*)
                                          placeholder*)
                           :style (merge (when inline? {:width "4rem"
                                                        :height "auto"
                                                        :margin 0
                                                        :text-align "center"})
                                         (when (some? type-width) {:width type-width})
                                         style)
                           :class (flatten [class (case valid?
                                                    true "valid"
                                                    false "invalid"
                                                    "")])
                           :auto-focus autofocus?
                           :disabled disabled?}
                          (when-not (empty? id) {:id id}))]
          #_(println {:internal-value @internal-value
                      :external-value @external-value})
          #_(println {:validators* validators*
                      :validate? validate?
                      :validation-error validation-error
                      :valid? valid?})
          #_(println {:default* default* :value-to-clean value-to-clean :clean-value clean-value})

          (if multiline?
            [:textarea.materialize-textarea attrs]
            [:input (assoc attrs :type "text")]))))))

(defn inline-input [attrs]
  [input (assoc attrs :inline? true)])

(defn input-with-label [args]
  [:<>
   [label args]
   [inline-input args]])

(defn inline-time-input [attrs]
  [inline-input (assoc attrs :type type-time)])

(defn textarea [_attrs]
  (let [!ref (atom nil)
        ref! (partial reset! !ref)
        resize! #(when (exists? js/jQuery)
                   (.trigger (js/jQuery (.querySelector @!ref "textarea")) "autoresize"))]
    (r/create-class
     {:component-did-mount resize!
      :component-did-update resize!
      :display-name "textarea"
      :reagent-render (fn [{:keys [id label-text value]
                            :as attrs}]
                        [:div.input-field
                         {:ref ref!
                          :style {:width "100%"}}
                         [input (into attrs {:multiline? true})]
                         (when label-text
                           [label {:id id
                                   :value value
                                   :label-text label-text}])])})))

(defn tooltip [_attrs]
  (let [hidden (r/atom true)]
    (fn [{:keys [icon content]}]
      [:span {:style {:position "relative"
                      :margin-left "0.5rem"
                      :cursor "help"}
              :on-mouse-over #(do (reset! hidden false) nil)
              :on-mouse-out #(do (reset! hidden true) nil)}
       [:span.blue-text {:class icon}]
       (when-not @hidden
         [:span {:style {:position "absolute"
                         :color "#000"
                         :font-size "14px"
                         :background "#fff"
                         ;; materialize css sets z-index to 999 for some elements
                         ;; so we need to use at least 1000 here so that the tooltip
                         ;; is always on top.
                         :z-index 1000
                         :bottom "2rem"
                         :padding "10px"
                         :box-shadow "3px 3px 3px #888"
                         :border "1px solid #000"
                         :width "20rem"}}
          content])])))

(defn- holiday-tooltip
  [school-holidays]
  [:div
   (for [[i [holiday-name states]] (map-indexed vector (sort school-holidays))]
     ^{:key holiday-name}
     [:div {:style {:margin-top (if (= i 0) "0" "0.5rem")}}
      [:strong holiday-name]
      [:table>tbody
       (for [[state holiday-range] (sort-by first states)]
         ^{:key (str state holiday-range)}
         [:tr
          [:td {:style {:padding "0"}} state]
          [:td {:style {:padding "0" :text-align "right"}} holiday-range]])]])])

(defn- date-info
  "Show weekday and possible holidays as well as a tooltip with relevant per-state holiday dates
  over a question mark icon."
  [value]
  (let [date (datetime/string->date value)
        [public-holidays school-holidays] (some-> date human/holidays)
        weekday (human/weekday-short date)
        is-school-holiday? (not (empty? school-holidays))
        info (str/join ", " (concat [weekday] public-holidays (when is-school-holiday? ["Ferien"])))]
    (when (and (not (nil? value))
               (not (str/blank? value)))
      [:span " (" (if (nil? date) "?" info)
       (when is-school-holiday?
         ^{:key "tooltip-button"}
         [tooltip {:icon "icon-info-sign"
                   :content [holiday-tooltip school-holidays]}])
       ")"])))

(defn inline-date-input [{:keys [value]}]
  (let [current-value (r/atom value)]
    (fn [{:keys [date-info?] :or {date-info? true} :as attrs}]
      [:span
       [inline-input (into attrs {:type type-date
                                  :on-change #(reset! current-value %)})]
       (when date-info? [date-info @current-value])])))

(defn block-input2
  "Features:
  - DONE block style
  - DONE label text
  - DONE label style
  - DONE label needs class active to move the label text up and away
  - shows validation error
  - class?"
  [_attrs]
  (let [focused? (r/atom false)]
    (fn [{:keys [id block-style label-text label-style value placeholder]
          :or {block-style {}
               label-style {}}
          :as attrs}]
      [:div.input-field {:style block-style}
       [input (into attrs
                    {:on-focus #(reset! focused? true)
                     :on-blur #(reset! focused? false)})]
       [:label (into {:for id
                      :style label-style}
                     (when (or @focused?
                               (not (or (nil? value) (str/blank? value)))
                               (not (empty? placeholder))) {:class "active"}))
        label-text]
       #_(when (and (not valid?) validation-error) [:span {:style {:color "red"}} validation-error])])))

(defn- get-choices [lookup-url query done fail]
  (GET lookup-url {:response-format :json
                   :keywords? true
                   :handler #(done (into [nil] (% :results)))
                   :error-handler (fn [{:keys [status status-text]}] (fail (str "Server-Fehler: " status ": " status-text)))
                   :params {:q query}}))

(defn select
  [{:keys [id value placeholder lookup-url choices on-save shown-entries]}]
  [single-dropdown
   :id id
   :choices (if lookup-url (fn [{:keys [filter-text]} done fail]
                             (get-choices lookup-url filter-text done fail))
                choices)
   :model value
   :label-fn #(if (some? %) (str (:text %) " (" (:id %) ")") "--------")
   :placeholder placeholder
   :width "300px"
   :max-height "400px"
   :filter-box? true
   :shown-entries shown-entries
   :id-fn identity
   :on-change on-save])

(defn block-select
  [{:keys [id block-style label-text label-style value on-save]
    :or {block-style {}
         label-style {}}
    :as attrs}]
  [:div.input-field {:style block-style}
   [select attrs]
   [:label {:for id
            :style label-style
            :class "active"}
    label-text]
   (when (some? value)
     [:div {:style {:display "inline-block"
                    :width "32px"
                    :height "32px"
                    :line-height "32px"
                    :text-align "center"}}
      [:a {:href "#"
           :on-click (comp #(on-save nil) machtfit/stop-event)
           :title "Auswahl zurücksetzen"}
       [:i {:class "icon-ban-circle"}]]])])

(defn select-box [{:keys [value on-change data no-widget-init? browser-default? required? style]
                   :or {style {}}}]
  [:select
   {:value value
    :required required?
    :on-change #(on-change (-> % .-target .-value))
    :class [(when no-widget-init? "no-widget-init") (when browser-default? "browser-default")]
    :style style}
   (for [x data
         :let [[value label'] (if (map? x)
                                [(:value x)
                                 (:label x)]
                                [x x])]]
     ^{:key (or value "")}
     [:option {:value (or value "")} label'])])

(defn- button [{:keys [title materialize-icon bootstrap-icon bootstrap-classes on-click]}]
  ;; TODO Possible refactoring, see https://github.com/machtfit/machtfit-oscar/pull/1251#discussion_r233526018
  (if (.querySelector js/document ".materialize-app")
    [:button.waves-effect.waves-light.btn {:on-click on-click}
     (when (some? materialize-icon)
       [:i.material-icons.left materialize-icon])
     title]
    [:button.btn {:on-click on-click
                  :class bootstrap-classes}
     (when (some? bootstrap-icon)
       [:i {:class bootstrap-icon}])
     title]))

(defn add-button [{:keys [title on-click]}]
  [button {:title (or title "Hinzufügen")
           :on-click on-click
           :materialize-icon "add"
           :bootstrap-icon "icon-plus"
           :bootstrap-classes "btn-success"}])

(defn edit-button [{:keys [title on-click]}]
  [button {:title (or title "Bearbeiten")
           :on-click on-click
           :bootstrap-icon "icon-edit"}])

(defn delete-button [{:keys [title on-click]}]
  [button {:title (or title "Löschen")
           :on-click on-click
           :materialize-icon "delete"
           :bootstrap-icon "icon-trash"
           :bootstrap-classes "btn-danger"}])

(defn finish-button [{:keys [title on-click]}]
  [button {:title (or title "Fertig")
           :on-click on-click
           :bootstrap-icon "icon-check"}])

(defn filter-input [{:keys [filter-text on-change]}]
  [:div.filter
   [inline-input {:value filter-text
                  :placeholder "Filter"
                  :style {:width "100%"
                          :text-align "left"}
                  :on-change #(on-change %)}]
   (when-not (empty? filter-text)
     (if (and (exists? js/jQuery) (> (.-length (js/jQuery ".materialize-app")) 0))
       [:a.btn-floating.grey
        {:style {:width "1.2rem"
                 :height "1.2rem"
                 :line-height "1.2rem"
                 :margin-left "0.5rem"
                 :position "absolute"
                 :right "0"
                 :top "0"}
         :on-click #(on-change "")}
        [:i.material-icons
         {:style {:font-size "1rem"
                  :line-height "1.2rem"}}
         "clear"]]
       [:a.btn {:on-click #(on-change "")}
        [:span {:aria-hidden "true"} "×"]]))])

(defn date-input [args]
  [:div {:style {:margin-bottom "1rem"}}
   [label args]
   [inline-date-input args]])

(defn checkbox [{:keys [checked? on-save label-text]}]
  (let [id (random-uuid)]
    [:div.col.s12 {:style {:margin-bottom "1rem"}}
     [:input.filled-in {:type "checkbox"
                        :id id
                        :checked checked?
                        :on-change #(let [new-value (-> % .-target .-value (= "on"))]
                                      (when on-save
                                        (on-save new-value)))}]
     [:label.black-text {:for id
                         :style {:height "auto"
                                 :line-height "normal"}}
      label-text]]))

(defn color-picker [{:keys [value on-change read-only? input-style label-text]}]
  (let [id (random-uuid)]
    [:span {:style {:margin-bottom "1rem"}}
     [:input {:type "color"
              :id id
              :value value
              :style input-style
              :disabled read-only?
              :on-change on-change}]
     [label {:id id
             :value value
             :label-text label-text}]]))
